import React, {
    Dispatch,
    FC,
    SetStateAction,
    useEffect,
    useRef,
    useState,
} from "react";
import {
    ButtonBox,
    ErrorValidationBox,
    ModelDialogBox,
    PaymentTypeDetails,
    TextBox,
    VoucherDetails,
} from "../";
import {
    ActionTypeEnum,
    PaymentConfigModel,
    PaymentTypeEnum,
    PaymentTypeItemsModel,
    PaymentValueDetailsModel,
    RequestActionModel,
    ValidationErrorModel,
} from "../../models";
import { getLabelName, updateStateDynamically } from "../../utils";
import { getPaymentConfiguration } from "../../serviceBroker/paymentTypeApiServiceBroker";
import { BsCashCoin } from "react-icons/bs";
import { FaCcMastercard } from "react-icons/fa";
import { BsThreeDots } from "react-icons/bs";
import { FaGift } from "react-icons/fa6";
import { TbMoneybag } from "react-icons/tb";
import "./paymentCss.css";

export const PaymentDetails: FC<{
    totalPayment: number;
    onActionEvent: (o: RequestActionModel) => void;
    setShowPaymentDetailsModel: Dispatch<SetStateAction<boolean>>;
}> = ({
          totalPayment,
          setShowPaymentDetailsModel,
          onActionEvent = () => {},
      }) => {
    //#region variables
    const initialValues: PaymentValueDetailsModel = {
        cash: 0,
        credit: 0,
        delay: 0,
        voucher: 0,
        totalAmount: 0,
        paymentTypeId: 0,
        remaining: totalPayment,
    };
    const defaultBtnValues: number[] = [7, 8, 9, 4, 5, 6, 1, 2, 3, 0];
    const extraBtnValues: number[] = [10, 50, 100, 500];
    const errorsMsg: ValidationErrorModel[] = [
        {
            MessageAr: getLabelName(
                "Sum of cash, credit and delay must be equal to total payment"
            ),
            MessageEn: getLabelName(
                "Sum of cash, credit and delay must be equal to total payment"
            ),
        },
    ];
    //#endregion
    //#region state
    const [showPaymentTypeDetailsModel, setShowPaymentTypeDetailsModel] =
        useState<boolean>(false);
    const firstInputRef = useRef<HTMLInputElement>(null);
    const [paymentValues, setPaymentValues] =
        useState<PaymentValueDetailsModel>(initialValues);
    const [selectedInput, setSelectedInput] = useState({
        name: "cash",
        value: 0,
    });
    const [errorMsg, setErrorMsg] = useState<ValidationErrorModel[]>([]);
    const [paymentTypeItems, setPaymentTypeItems] = useState<
        PaymentTypeItemsModel[]
    >([]);
    const [paymentConfig, setPaymentConfig] = useState<PaymentConfigModel>({
        IsMultipleCash: false,
        IsMultipleDelay: false,
        IsMultipleCard: false,
        IsMultipleOther: false,
        ParentPaymentCashId: 0,
        ParentPaymentCardId: 0,
        ParentPaymentDelayId: 0,
        ParentMultipleId: 0,
        CashPaymentTypeList: null,
        CardPaymentTypeList: null,
        DelayPaymentTypeList: null,
    });
    const [clickedPaymentType, setClickedPaymentType] = useState<string>("");
    //#endregion
    //#region UseEffect
    useEffect(() => {
        if (firstInputRef.current) {
            firstInputRef.current.focus();
        }
        const fillData = async () => {
            await getPaymentConfigurationResult();
        };
        fillData().then(() => {});
    }, []);
    //#endregion
    //#region functions
    //#region textBox Events
    const handleChangeInput = async (e: React.ChangeEvent<any>) => {
        if (e.target.value) {
            e.target.value = e.target.value.replace(/^0/, "");
            setErrorMsg([]);
        }
        const obj: PaymentValueDetailsModel = {
            ...paymentValues,
            [e.target.name]: Number(e.target.value),
        };
        const totalCalculatedValue: number = calculatePaymentTotalValues(obj);
        const inputValueCheck = calculatePaymentTotalValues(obj) <= totalPayment;
        await updateStateDynamically(setPaymentValues, paymentValues, null, null, [
            {
                key: e.target.name,
                value:
                    inputValueCheck || e.target.name === "cash"
                        ? Number(e.target.value)
                        : e.target.name === "cash"
                            ? paymentValues.cash
                            : e.target.name === "credit"
                                ? paymentValues.credit
                                : e.target.name === "delay"
                                    ? paymentValues.delay
                                    : "",
            },
            {
                key: "remaining",
                value: calculateRemainingValue(
                    totalPayment,
                    totalCalculatedValue,
                    e.target.name
                ),
            },
        ]);
    };
    const handleBlurInput = async (e: React.ChangeEvent<any>) => {
        setSelectedInput((prev) => ({
            ...prev,
            name: e.target.name,
            value: Number(e.target.value),
        }));
    };
    //#endregion
    //#region button Events
    const handleClickBtn = async (e: React.ChangeEvent<any>) => {
        switch (e.target.name) {
            case "clear":
                const clearedObject: PaymentValueDetailsModel = {
                    ...paymentValues,
                    [selectedInput.name]: 0,
                };
                const totalCalculatedClearedValues: number =
                    calculatePaymentTotalValues(clearedObject);
                await updateStateDynamically(
                    setPaymentValues,
                    paymentValues,
                    null,
                    null,
                    [
                        { key: selectedInput.name, value: 0 },
                        {
                            key: "remaining",
                            value: totalPayment - totalCalculatedClearedValues,
                        },
                    ]
                );
                break;
            case "float":
                setPaymentValues((prevState) => ({
                    ...prevState,
                    [selectedInput.name]: Number(selectedInput.value) * 0.1,
                }));
                break;
            default:
                const isDefaultButton: boolean =
                    e.currentTarget.getAttribute("datatype") === "default";
                const currentValue: number = Number(e.target.value);
                let propertyValue: number = 0;
                if (selectedInput.name !== "") {
                    const propertyName: keyof PaymentValueDetailsModel =
                        selectedInput.name as keyof PaymentValueDetailsModel;
                    propertyValue = isDefaultButton
                        ? parseInt(`${paymentValues[propertyName]}${currentValue}`, 10) //paymentValues[propertyName] + currentValue
                        : currentValue; // Number(paymentValues[propertyName]+currentValue);
                } else {
                    propertyValue = currentValue;
                }
                const updatedObject: PaymentValueDetailsModel = {
                    ...paymentValues,
                    [selectedInput.name]: propertyValue,
                };
                const totalCalculatedValue: number =
                    calculatePaymentTotalValues(updatedObject);
                const btnValueCheck = totalCalculatedValue <= totalPayment;
                await updateStateDynamically(
                    setPaymentValues,
                    paymentValues,
                    null,
                    null,
                    [
                        {
                            key: selectedInput.name,
                            value:
                                btnValueCheck || selectedInput.name === "cash"
                                    ? propertyValue
                                    : selectedInput.name === "cash"
                                        ? paymentValues.cash
                                        : selectedInput.name === "credit"
                                            ? paymentValues.credit
                                            : selectedInput.name === "delay"
                                                ? paymentValues.delay
                                                : "",
                        },
                        {
                            key: "remaining",
                            value: calculateRemainingValue(
                                totalPayment,
                                totalCalculatedValue,
                                selectedInput.name
                            ),
                        },
                    ]
                );
                break;
        }
    };
    const handleClickPaymentItemBtn = (
        e: React.MouseEvent<HTMLButtonElement>
    ) => {
        const { name } = e.target as HTMLButtonElement;

        setClickedPaymentType(name);
        const dataType = e.currentTarget.getAttribute("datatype") || "-";
        const key = dataType as keyof PaymentConfigModel;
        const paymentTypeItemsArray: PaymentTypeItemsModel[] =
            paymentConfig[key] && Array.isArray(paymentConfig[key])
                ? (paymentConfig[key] as PaymentTypeItemsModel[])
                : [];
        setPaymentTypeItems(paymentTypeItemsArray);
        setShowPaymentTypeDetailsModel(
            paymentTypeItemsArray !== null && paymentTypeItemsArray.length !== 0
        );
        setPaymentValues(calculateTextboxValueOnButtonClick(paymentValues, name));
    };
    //#endregion
    const calculatePaymentTotalValues = (
        request: PaymentValueDetailsModel
    ): number => {
        return request !== null && request !== undefined
            ? Number(request.cash) +
            Number(request.credit) +
            Number(request.delay) +
            Number(request.voucher)
            : 0;
    };
    const handleSave = async () => {
        const totalCalculatedValue: number =
            calculatePaymentTotalValues(paymentValues);
        if (
            totalCalculatedValue === Number(totalPayment) ||
            paymentValues.cash >= Number(totalPayment)
        ) {
            onActionEvent({
                request: paymentValues,
                action: ActionTypeEnum.AddPaymentValuesSuccess,
            });
        } else {
            setErrorMsg(errorsMsg);
        }
        await updatePaymentValuesV2(totalCalculatedValue);
    };
    const updatePaymentValuesV2 = async (totalCalculatedValue: number) => {
        const paymentTypePriorities: PaymentTypeEnum[] = [
            PaymentTypeEnum.Cash,
            PaymentTypeEnum.Credit,
            PaymentTypeEnum.Delay,
            // Add more payment types as needed
        ];

        let selectedPaymentType: PaymentTypeEnum = PaymentTypeEnum.None;
        let countNonZeroProps = 0;
        for (const paymentType of paymentTypePriorities) {
            // @ts-ignore
            if (paymentValues[PaymentTypeEnum[paymentType].toLowerCase()] > 0) {
                countNonZeroProps++;
            }
        }
        if (countNonZeroProps === 1) {
            for (const paymentType of paymentTypePriorities) {
                // @ts-ignore
                if (paymentValues[PaymentTypeEnum[paymentType].toLowerCase()] > 0) {
                    selectedPaymentType = paymentType;
                    break;
                }
            }
        } else if (countNonZeroProps > 1) {
            // More than one payment type has a non-zero value
            selectedPaymentType = PaymentTypeEnum.Multiple;
        }
        if (selectedPaymentType === PaymentTypeEnum.None) {
            // Check if all payment values are zero
            const allZero = Object.values(paymentValues).every(
                (value) => value === 0
            );
            if (allZero) {
                selectedPaymentType = PaymentTypeEnum.None;
            }
        }
        // @ts-ignore
        const updates = [
            {
                key: "paymentTypeId",
                value:
                    paymentValues.paymentTypeId === 0
                        ? selectedPaymentType
                        : paymentValues.paymentTypeId,
            },
            { key: "totalAmount", value: totalCalculatedValue },
            {
                key: "cash",
                value:
                    selectedPaymentType === PaymentTypeEnum.Cash
                        ? totalPayment
                        : Number(paymentValues["cash"]),
            },
        ];
        await updateStateDynamically(
            setPaymentValues,
            paymentValues,
            null,
            null,
            updates
        );
    };
    const getPaymentConfigurationResult = async () => {
        const res = await getPaymentConfiguration();
        if (res.Result) {
            setPaymentConfig(res.Result);
        }
    };
    const calculateRemainingValue = (
        totalPayment: number,
        totalCalculatedValues: number,
        paymentType: string
    ): number => {
        let value: number;
        value =
            paymentType === "cash"
                ? totalPayment - totalCalculatedValues < 0
                    ? 0
                    : totalPayment - totalCalculatedValues
                : totalCalculatedValues <= totalPayment
                    ? totalPayment - totalCalculatedValues
                    : paymentValues.remaining;
        return value;
    };

    function calculateTextboxValueOnButtonClick(
        paymentValues: PaymentValueDetailsModel,
        name: string
    ): PaymentValueDetailsModel {
        // Create a copy to avoid mutating the original object
        const updatedValues = { ...paymentValues };
        switch (name.toLowerCase()) {
            case "credit":
                updatedValues.credit = updatedValues.credit
                    ? updatedValues.credit
                    : paymentValues.remaining;
                break;
            case "cash":
                updatedValues.cash = updatedValues.cash
                    ? updatedValues.cash
                    : paymentValues.remaining;
                break;
            case "delay":
                updatedValues.delay = updatedValues.delay
                    ? updatedValues.delay
                    : paymentValues.remaining;
                break;
        }
        updatedValues.remaining =
            totalPayment -
            (updatedValues.credit + updatedValues.cash + updatedValues.delay);
        return updatedValues;
    }

    //#endregion
    //#region html
    return (
        <>
            <div className="d-flex justify-content-center p-2">
                <div className="d-flex" style={{ width: "100%" }}>
                    {/* Left Panel */}
                    <div className="col-4 payment-container">
                        <div className="payment-summary-wrapper">
                            <div className="payment-box">
                                <p className="payment-box-text">
                                    {getLabelName("Total")} :{" "}
                                    <span className="fw-bold payment-box-value">
                    {totalPayment}
                  </span>
                                </p>
                            </div>
                            <div className="payment-box">
                                <p className="payment-box-text">
                                    {getLabelName("Rest")} :{" "}
                                    <span className="fw-bold payment-box-value">
                    {paymentValues.remaining}
                  </span>
                                </p>
                            </div>
                        </div>

                        <div className="payment-buttons-grid">
                            {defaultBtnValues.map((value, index) => (
                                <ButtonBox
                                    className="custom-btn-style"
                                    variant="none"
                                    value={value}
                                    type="button"
                                    datatype="default"
                                    onClick={handleClickBtn}
                                    key={`default_btn_${index}`}
                                >
                                    {value}
                                </ButtonBox>
                            ))}
                            {extraBtnValues.map((value, index) => (
                                <ButtonBox
                                    key={`extra_btn_${index}`}
                                    className="custom-btn-style"
                                    variant="none"
                                    value={value}
                                    type="button"
                                    datatype="extra"
                                    onClick={handleClickBtn}
                                >
                                    {value}
                                </ButtonBox>
                            ))}
                            <span>
                <button
                    className="custom-btn-style red-btn"
                    name="clear"
                    type="button"
                    onClick={handleClickBtn}
                >
                  C
                </button>
              </span>
                        </div>
                    </div>
                    {/* Center Panel */}
                    <div className="col-4 mx-4 payment-center-panel">
                        {errorMsg.length > 0 && <ErrorValidationBox errors={errorMsg} />}

                        <div className="d-flex justify-content-center my-3 payment-header">
                            <h4 className="payment-header-title">
                                {getLabelName("Select Payment Mode")}
                            </h4>
                        </div>

                        <div className="payment-section">
                            {/* Cash */}
                            <div className="mb-2">
                                <div className="payment-method-label">
                  <span role="img" aria-label="icon">
                    <BsCashCoin />
                  </span>
                                    <label htmlFor="cash">{getLabelName("Cash Amount")} </label>
                                </div>
                                <div className="payment-input-group">
                                    <div className="payment-input-wrapper">
                                        <input
                                            type="number"
                                            name="cash"
                                            value={paymentValues.cash}
                                            onChange={handleChangeInput}
                                            onBlur={handleBlurInput}
                                            ref={firstInputRef}
                                            className="payment-input"
                                        />
                                    </div>
                                    <div>
                                        <ButtonBox
                                            iconType="cash-multiple"
                                            variant="info"
                                            name="cash"
                                            style={{ width: "100%" }}
                                            onClick={handleClickPaymentItemBtn}
                                            datatype="CashPaymentTypeList"
                                            disabled={
                                                !paymentConfig.IsMultipleCash ||
                                                paymentValues.remaining === 0
                                            }
                                        >
                                            {getLabelName("Cash")}
                                        </ButtonBox>
                                    </div>
                                </div>
                            </div>

                            {/* Card */}
                            <div className="mb-2">
                                <div className="payment-method-label">
                  <span role="img" aria-label="icon">
                    <FaCcMastercard />
                  </span>
                                    <label htmlFor="credit">{getLabelName("Card Amount")}</label>
                                </div>
                                <div className="payment-input-group">
                                    <div className="payment-input-wrapper">
                                        <input
                                            type="number"
                                            name="credit"
                                            value={paymentValues.credit}
                                            onChange={handleChangeInput}
                                            onBlur={handleBlurInput}
                                            className="payment-input"
                                            placeholder="Enter cash amount"
                                        />
                                    </div>
                                    <div>
                                        <ButtonBox
                                            iconType="credit-card"
                                            variant="info"
                                            name="credit"
                                            style={{ width: "100%" }}
                                            onClick={handleClickPaymentItemBtn}
                                            datatype="CardPaymentTypeList"
                                            disabled={
                                                !paymentConfig.IsMultipleCard ||
                                                paymentValues.remaining === 0
                                            }
                                        >
                                            {getLabelName("Card")}
                                        </ButtonBox>
                                    </div>
                                </div>
                            </div>

                            {/* Delay */}
                            <div className="mb-2">
                                <div className="payment-method-label">
                  <span role="img" aria-label="icon">
                    <BsThreeDots />
                  </span>
                                    <label htmlFor="delay">{getLabelName("Delay Value")}</label>
                                </div>
                                <div className="payment-input-group">
                                    <div className="payment-input-wrapper">
                                        <input
                                            type="number"
                                            name="delay"
                                            value={paymentValues.delay}
                                            onChange={handleChangeInput}
                                            onBlur={handleBlurInput}
                                            className="payment-input"
                                            placeholder="Enter cash amount"
                                        />
                                    </div>
                                    <div>
                                        <ButtonBox
                                            iconType="dots-horizontal"
                                            variant="info"
                                            name="delay"
                                            style={{ width: "100%" }}
                                            onClick={handleClickPaymentItemBtn}
                                            datatype="DelayPaymentTypeList"
                                            disabled={
                                                !paymentConfig.IsMultipleDelay ||
                                                paymentValues.remaining === 0
                                            }
                                        >
                                            {getLabelName("Delay")}
                                        </ButtonBox>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Right Panel */}
                    <div className="col-3 payment-right-panel">
                        <div className="payment-voucher-box">
                            <div className="payment-voucher-header">
                <span role="img" aria-label="icon">
                  <FaGift />
                </span>
                                <h4 className="payment-voucher-title">Voucher</h4>
                            </div>
                            <p className="payment-voucher-subtext">
                                Apply the offered discount coupons now for an exclusive special
                                discount.
                            </p>
                            <div className="payment-voucher-input-group">
                                <div style={{ flexGrow: 1 }}>
                                    <input
                                        className="payment-voucher-input"
                                        placeholder="Enter Voucher Code"
                                    />
                                </div>
                                <div>
                                    <ButtonBox
                                        variant="info"
                                        iconType="check-circle"
                                        style={{ padding: "8px 16px" }}
                                    >
                                        {getLabelName("Confirm")}
                                    </ButtonBox>
                                </div>
                            </div>
                        </div>

                        <div className="payment-summary-box">
                            <div className="payment-summary-content">
                                {[
                                    { label: "Change", value: paymentValues.remaining },
                                    { label: "Cash", value: paymentValues.cash },
                                    { label: "Card", value: paymentValues.credit },
                                    { label: "Delay", value: paymentValues.delay },
                                    { label: "Voucher", value: "0" },
                                ].map((item, idx) => {
                                    const isChange = item.label === "Change";
                                    return (
                                        <div
                                            key={idx}
                                            className={`payment-summary-row ${
                                                isChange ? "change" : "normal"
                                            }`}
                                        >
                                            <span>{item.label}</span>
                                            <span>{item.value}</span>
                                        </div>
                                    );
                                })}

                                <hr style={{ margin: "10px 0", borderColor: "#ccc" }} />

                                <div className="payment-summary-total">
                                    <span>Total</span>
                                    <span>{totalPayment}</span>
                                </div>
                            </div>

                            <div className="payment-action-buttons">
                                <button className="payment-confirm-btn" onClick={handleSave}>
                                    <TbMoneybag size={18} /> {getLabelName("Pay")}
                                </button>

                                <ButtonBox
                                    iconType="close-circle"
                                    variant="danger"
                                    style={{ width: "100%" }}
                                    onClick={() => {
                                        onActionEvent({
                                            action: ActionTypeEnum.AddPaymentValuesClear,
                                        });
                                        setShowPaymentDetailsModel(false);
                                    }}
                                >
                                    {getLabelName("Cancel")}
                                </ButtonBox>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
    //#endregion
};
